// E-Edition upload (client portal E-Editions page and Admin Review → E-Editions). Pick the paper
// and edition date, drop the page PDFs (each sent on its own, so a page can be very large), check
// the order, then Publish: the server builds the edition (conjoin), the front-page pictures and —
// for flipbook papers — the FlipHTML5 flipbook, and sends them to site1. Backend: src/eedition.js.
import { useState, useEffect, useRef, useCallback } from "react";
import { Upload, FileText, ArrowUp, ArrowDown, X, Check, AlertTriangle, Loader2, Send, RotateCcw, Circle } from "lucide-react";

const fmtSize = (n) => (n >= 1e9 ? `${(n / 1e9).toFixed(1)} GB` : n >= 1e6 ? `${(n / 1e6).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1e3))} KB`);
const fmtDate = (d) => new Date(`${String(d).slice(0, 10)}T12:00:00`).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });
const byName = (a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" });

// pal: colours for the surface it sits on (the portal and the staff app look different)
export default function EEditionUpload({ client, base, publications, today, pal, onDone }) {
  const [pubId, setPubId] = useState(publications.length === 1 ? publications[0].id : "");
  const [date, setDate] = useState(today);
  const [draft, setDraft] = useState(null);        // the run while it's being put together
  const [pages, setPages] = useState([]);          // [{id, name, size, pages, ok, problem}] in order
  const [sending, setSending] = useState([]);      // files still going up: [{key, name, size, pct, error}]
  const [run, setRun] = useState(null);            // after Publish
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [over, setOver] = useState(false);
  const pick = useRef(null);
  const draftRef = useRef(null);
  draftRef.current = draft;

  const pub = publications.find((p) => p.id === pubId);
  const errOf = (e, d) => e?.response?.data?.error || d;

  const ensureDraft = async () => {
    if (draftRef.current) return draftRef.current;
    const r = await client.post(`${base}/drafts`, { publication_id: pubId, edition_date: date });
    setDraft(r.data); draftRef.current = r.data;
    return r.data;
  };

  const addFiles = async (list) => {
    setError("");
    if (!pubId) { setError("Choose the paper first."); return; }
    const files = Array.from(list || []).filter((f) => f.size > 0);
    if (!files.length) return;
    let d;
    try { d = await ensureDraft(); } catch (e) { setError(errOf(e, "Couldn't start the upload.")); return; }
    const queue = files.sort(byName).map((f, i) => ({ key: `${Date.now()}-${i}`, name: f.name, size: f.size, pct: 0, file: f }));
    setSending((s) => [...s, ...queue.map(({ file, ...q }) => q)]);
    for (const q of queue) {
      const fd = new FormData(); fd.append("file", q.file, q.name);
      try {
        const r = await client.post(`${base}/drafts/${d.id}/pages`, fd, {
          headers: { "Content-Type": "multipart/form-data" }, timeout: 0,
          onUploadProgress: (ev) => setSending((s) => s.map((x) => (x.key === q.key ? { ...x, pct: ev.total ? Math.round((ev.loaded / ev.total) * 100) : x.pct } : x))),
        });
        setPages((ps) => { const byId = new Map([...ps, r.data.page].map((p) => [p.id, p])); return r.data.order.map((id) => byId.get(id)).filter(Boolean); });
        setSending((s) => s.filter((x) => x.key !== q.key));
      } catch (e) {
        setSending((s) => s.map((x) => (x.key === q.key ? { ...x, error: errOf(e, "Upload failed") } : x)));
      }
    }
  };

  const move = async (i, dir) => {
    const j = i + dir; if (j < 0 || j >= pages.length) return;
    const next = pages.slice(); [next[i], next[j]] = [next[j], next[i]];
    setPages(next);
    try { await client.patch(`${base}/drafts/${draft.id}`, { order: next.map((p) => p.id) }); } catch (e) { setError(errOf(e, "Couldn't save the order.")); }
  };
  const remove = async (p) => {
    try { await client.delete(`${base}/drafts/${draft.id}/pages/${p.id}`); setPages((ps) => ps.filter((x) => x.id !== p.id)); }
    catch (e) { setError(errOf(e, "Couldn't remove that page.")); }
  };
  const changeDate = async (v) => {
    setDate(v);
    if (draft && v) { try { const r = await client.patch(`${base}/drafts/${draft.id}`, { edition_date: v }); setDraft(r.data); } catch (e) { setError(errOf(e, "Couldn't change the date.")); } }
  };
  const reset = useCallback(async (discard) => {
    if (discard && draftRef.current) { try { await client.delete(`${base}/drafts/${draftRef.current.id}`); } catch { /* already gone */ } }
    setDraft(null); draftRef.current = null; setPages([]); setSending([]); setRun(null); setError("");
  }, [client, base]);
  const changePub = async (v) => {
    if (draft && (pages.length || sending.length) && !window.confirm("Switch paper? The pages added so far will be removed.")) return;
    await reset(true); setPubId(v);
  };

  const publish = async (replace = false) => {
    setBusy(true); setError("");
    try {
      const r = await client.post(`${base}/drafts/${draft.id}/submit`, { replace });
      setRun(r.data);
    } catch (e) {
      if (e?.response?.status === 409 && e.response.data?.exists) {
        if (window.confirm(e.response.data.error)) { setBusy(false); return publish(true); }
      } else setError(errOf(e, "Couldn't publish."));
    } finally { setBusy(false); }
  };

  // Follow the run until it's finished.
  useEffect(() => {
    if (!run || ["done", "failed"].includes(run.status)) return undefined;
    const t = setTimeout(async () => {
      try { const r = await client.get(`${base}/runs/${run.id}`); setRun(r.data); if (["done", "failed"].includes(r.data.status) && onDone) onDone(r.data); }
      catch { /* try again next tick */ }
    }, 2500);
    return () => clearTimeout(t);
  }, [run, client, base, onDone]);

  const field = { width: "100%", height: 40, padding: "0 12px", borderRadius: 8, border: `1px solid ${pal.border}`, background: pal.input, color: pal.text, fontSize: 14, fontFamily: "inherit" };
  const label = { display: "block", fontSize: 12, fontWeight: 700, color: pal.muted, marginBottom: 6 };
  const btn = (primary, disabled) => ({ display: "inline-flex", alignItems: "center", gap: 7, height: 40, padding: "0 18px", borderRadius: 8, fontSize: 14, fontWeight: 700,
    fontFamily: "inherit", cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1,
    border: primary ? "none" : `1px solid ${pal.border}`, background: primary ? pal.accent : "transparent", color: primary ? "#fff" : pal.text });
  const bad = pages.filter((p) => !p.ok);
  const canPublish = !!draft && pages.length > 0 && !bad.length && !sending.some((s) => !s.error) && !busy;

  if (run) {
    const icon = (s) => s.status === "done" ? <Check size={15} color={pal.ok} /> : s.status === "failed" ? <X size={15} color={pal.danger} />
      : s.status === "running" ? <Loader2 size={15} color={pal.accent} className="ee-spin" /> : <Circle size={13} color={pal.faint} />;
    return (
      <div>
        <style>{`@keyframes ee-spin{to{transform:rotate(360deg)}}.ee-spin{animation:ee-spin 1s linear infinite}`}</style>
        <div style={{ fontSize: 16, fontWeight: 800, color: pal.text, marginBottom: 4 }}>
          {run.status === "done" ? "Published" : run.status === "failed" ? "Stopped" : "Publishing…"} — {pub?.name} · {fmtDate(run.edition_date)}
        </div>
        <div style={{ fontSize: 13, color: pal.muted, marginBottom: 14 }}>{run.base_name}.pdf · {run.pages.length} page file{run.pages.length === 1 ? "" : "s"}</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
          {(run.steps.length ? run.steps : [{ key: "q", label: "Waiting to start", status: "pending" }]).map((s) => (
            <div key={s.key} style={{ display: "flex", alignItems: "flex-start", gap: 10, fontSize: 14, color: s.status === "pending" ? pal.faint : pal.text }}>
              <span style={{ width: 18, display: "inline-flex", justifyContent: "center", marginTop: 2 }}>{icon(s)}</span>
              <span style={{ flex: 1 }}>{s.label}{s.detail && <span style={{ display: "block", fontSize: 12, color: s.status === "failed" ? pal.danger : pal.muted, marginTop: 2, overflowWrap: "anywhere" }}>{s.detail}</span>}</span>
            </div>
          ))}
        </div>
        {run.status === "failed" && <div style={{ fontSize: 13, color: pal.danger, marginBottom: 12 }}>MIDTC has been shown this error and can re-run it for you.</div>}
        {["done", "failed"].includes(run.status) && <button type="button" style={btn(true)} onClick={() => reset(false)}><RotateCcw size={15} /> Upload another edition</button>}
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0,2fr) minmax(0,1fr)", gap: 12, marginBottom: 14 }}>
        <label><span style={label}>Paper</span>
          <select value={pubId} onChange={(e) => changePub(e.target.value)} style={field}>
            {publications.length !== 1 && <option value="">Choose the paper…</option>}
            {publications.map((p) => <option key={p.id} value={p.id}>{p.name}{p.flip_enabled ? " (with flipbook)" : ""}</option>)}
          </select>
        </label>
        <label><span style={label}>Edition date</span>
          <input type="date" value={date} onChange={(e) => changeDate(e.target.value)} style={field} />
        </label>
      </div>

      <div onClick={() => pick.current?.click()} onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
        onDrop={(e) => { e.preventDefault(); setOver(false); addFiles(e.dataTransfer.files); }}
        style={{ border: `2px dashed ${over ? pal.accent : pal.border}`, borderRadius: 12, padding: "26px 16px", textAlign: "center", cursor: "pointer",
          background: over ? pal.hover : "transparent", color: pal.muted, marginBottom: 14 }}>
        <Upload size={26} color={pal.accent} />
        <div style={{ fontSize: 15, fontWeight: 700, color: pal.text, marginTop: 8 }}>Drop the page PDFs here</div>
        <div style={{ fontSize: 13, marginTop: 3 }}>or click to choose them · they're put in file-name order, which you can change below</div>
        <input ref={pick} type="file" accept="application/pdf,.pdf" multiple style={{ display: "none" }} onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
      </div>

      {(pages.length > 0 || sending.length > 0) && (
        <div style={{ border: `1px solid ${pal.border}`, borderRadius: 10, overflow: "hidden", marginBottom: 14 }}>
          {pages.map((p, i) => (
            <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", borderBottom: `1px solid ${pal.border}`, fontSize: 13.5 }}>
              <span style={{ width: 26, color: pal.faint, fontVariantNumeric: "tabular-nums" }}>{i + 1}</span>
              {p.ok ? <FileText size={15} color={pal.muted} /> : <AlertTriangle size={15} color={pal.danger} />}
              <span style={{ flex: 1, minWidth: 0, color: pal.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {p.name} <span style={{ color: p.ok ? pal.faint : pal.danger }}>· {p.ok ? `${p.pages} page${p.pages === 1 ? "" : "s"} · ${fmtSize(p.size)}` : p.problem}</span>
              </span>
              <button type="button" aria-label="Move up" title="Move up" disabled={i === 0} onClick={() => move(i, -1)} style={{ ...btn(false, i === 0), height: 28, padding: "0 6px" }}><ArrowUp size={14} /></button>
              <button type="button" aria-label="Move down" title="Move down" disabled={i === pages.length - 1} onClick={() => move(i, 1)} style={{ ...btn(false, i === pages.length - 1), height: 28, padding: "0 6px" }}><ArrowDown size={14} /></button>
              <button type="button" aria-label="Remove" title="Remove" onClick={() => remove(p)} style={{ ...btn(false), height: 28, padding: "0 6px", color: pal.danger }}><X size={14} /></button>
            </div>
          ))}
          {sending.map((s) => (
            <div key={s.key} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", borderBottom: `1px solid ${pal.border}`, fontSize: 13.5 }}>
              <span style={{ width: 26 }} />
              {s.error ? <AlertTriangle size={15} color={pal.danger} /> : <Loader2 size={15} color={pal.accent} className="ee-spin" />}
              <span style={{ flex: 1, minWidth: 0, color: pal.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {s.name} <span style={{ color: s.error ? pal.danger : pal.faint }}>· {s.error || `uploading ${s.pct}% of ${fmtSize(s.size)}`}</span>
              </span>
              {s.error && <button type="button" aria-label="Dismiss" onClick={() => setSending((x) => x.filter((y) => y.key !== s.key))} style={{ ...btn(false), height: 28, padding: "0 6px" }}><X size={14} /></button>}
            </div>
          ))}
          <style>{`@keyframes ee-spin{to{transform:rotate(360deg)}}.ee-spin{animation:ee-spin 1s linear infinite}`}</style>
        </div>
      )}

      {draft && <div style={{ fontSize: 13, color: pal.muted, marginBottom: 12 }}>Will publish as <b style={{ color: pal.text }}>{draft.base_name}.pdf</b>{pub?.flip_enabled ? " and its flipbook" : ""} for {fmtDate(draft.edition_date)}.</div>}
      {bad.length > 0 && <div style={{ fontSize: 13, color: pal.danger, marginBottom: 10 }}>Remove or replace the page{bad.length === 1 ? "" : "s"} marked in red before publishing.</div>}
      {error && <div role="alert" style={{ fontSize: 13, color: pal.danger, marginBottom: 10 }}>{error}</div>}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        <button type="button" disabled={!canPublish} onClick={() => publish(false)} style={btn(true, !canPublish)}>
          {busy ? <Loader2 size={15} className="ee-spin" /> : <Send size={15} />} Publish edition
        </button>
        {draft && <button type="button" onClick={() => { if (window.confirm("Remove these pages and start over?")) reset(true); }} style={btn(false)}>Start over</button>}
      </div>
    </div>
  );
}

const STATUS = { queued: "Waiting", running: "Publishing", done: "Published", failed: "Stopped" };
// Recent editions. onRetry (staff only) re-runs a stopped one.
export function EditionList({ runs, pal, onRetry, showError }) {
  if (!runs?.length) return <div style={{ fontSize: 13.5, color: pal.muted }}>No editions uploaded here yet.</div>;
  const color = (s) => (s === "done" ? pal.ok : s === "failed" ? pal.danger : pal.accent);
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13.5, minWidth: 620 }}>
        <thead><tr>{["Edition date", "Paper", "File", "Status", "By", ""].map((h) => (
          <th key={h} style={{ textAlign: "left", fontSize: 11, fontWeight: 700, letterSpacing: ".05em", textTransform: "uppercase", color: pal.faint, padding: "8px 10px", borderBottom: `1px solid ${pal.border}` }}>{h}</th>))}</tr></thead>
        <tbody>
          {runs.map((r) => (
            <tr key={r.id} style={{ borderBottom: `1px solid ${pal.border}` }}>
              <td style={{ padding: "9px 10px", color: pal.text, whiteSpace: "nowrap" }}>{fmtDate(r.edition_date)}</td>
              <td style={{ padding: "9px 10px", color: pal.text }}>{r.publication}</td>
              <td style={{ padding: "9px 10px", color: pal.muted, fontFamily: "ui-monospace, Menlo, monospace", fontSize: 12.5 }}>{r.base_name}.pdf</td>
              <td style={{ padding: "9px 10px", color: color(r.status), fontWeight: 700, whiteSpace: "nowrap" }}>
                {STATUS[r.status] || r.status}
                {showError && r.status === "failed" && r.error && <div style={{ fontWeight: 400, fontSize: 12, color: pal.danger, maxWidth: 380, whiteSpace: "normal", overflowWrap: "anywhere" }}>{r.error}</div>}
              </td>
              <td style={{ padding: "9px 10px", color: pal.muted, whiteSpace: "nowrap" }}>{r.created_by_name}</td>
              <td style={{ padding: "9px 10px", textAlign: "right" }}>
                {onRetry && r.status === "failed" && <button type="button" onClick={() => onRetry(r)} style={{ border: `1px solid ${pal.border}`, background: "transparent", color: pal.text, borderRadius: 7, padding: "5px 10px", fontSize: 12.5, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>Run again</button>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
